// components/historial-pagos/HistorialPagosPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    FileText,
    Search,
    FileSpreadsheet,
    Calendar,
    User,
    DollarSign,
    CreditCard,
    CheckCircle,
    AlertCircle,
    ArrowLeft,
    X
} from 'lucide-react';
import { buscarCitaPorCodigo, ReportePago } from '../../services/reportePagosService';
import { fetchCitasPorUsuarioCompleto } from '../../services/citasService';
import { fetchUsuarioById } from '../../services/userService';
import { exportToPDF, exportToExcel } from './exportadores';
import { Link, useParams } from 'react-router-dom';
import './HistorialPagosPage.css';

const HistorialPagosPage = () => {
    const { usuarioId } = useParams<{ usuarioId?: string }>();
    const [codigoBusqueda, setCodigoBusqueda] = useState('');
    const [reporte, setReporte] = useState<ReportePago | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [citasCliente, setCitasCliente] = useState<Array<{
        id: number;
        codigo: string;
        estado: string;
        estado_pago: string;
        total: number;
        monto_pagado: number;
        fecha_inicio: string;
    }>>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLoadingCitas, setIsLoadingCitas] = useState(false);
    const [usuarioInfo, setUsuarioInfo] = useState<{
        id: number;
        nombre: string;
    } | null>(null);
    const [citasFiltradas, setCitasFiltradas] = useState<any[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Determinar si estamos en el contexto de un cliente específico
    const esContextoCliente = !!usuarioId;

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Cargar citas del cliente si estamos en su contexto
    useEffect(() => {
        if (esContextoCliente) {
            const cargarCitasCliente = async () => {
                setIsLoadingCitas(true);
                try {
                    // Cargar información del usuario
                    const usuarioData = await fetchUsuarioById(parseInt(usuarioId!));
                    setUsuarioInfo({
                        id: usuarioData.id,
                        nombre: `${usuarioData.first_name} ${usuarioData.last_name}`
                    });

                    // Cargar todas las citas del usuario
                    const todasLasCitas = await fetchCitasPorUsuarioCompleto(usuarioData.id);

                    // Formatear citas para el dropdown - NORMALIZAR CAMPOS
                    const citasFormateadas = todasLasCitas.map(cita => ({
                        id: cita.id,
                        codigo: cita.codigo || '',
                        estado: cita.estado || '',
                        estado_pago: cita.estado_pago || '',
                        total: Number(cita.total) || 0,
                        monto_pagado: Number(cita.monto_pagado || 0),
                        fecha_inicio: cita.fecha_inicio || ''
                    }));

                    // Ordenar por fecha más reciente
                    citasFormateadas.sort((a, b) =>
                        new Date(b.fecha_inicio).getTime() - new Date(a.fecha_inicio).getTime()
                    );

                    setCitasCliente(citasFormateadas);
                    setCitasFiltradas(citasFormateadas);
                } catch (err) {
                    console.error('Error al cargar citas del cliente:', err);
                } finally {
                    setIsLoadingCitas(false);
                }
            };

            cargarCitasCliente();
        }
    }, [usuarioId, esContextoCliente]);

    // Filtrar citas cuando cambia la búsqueda - CON PROTECCIÓN CONTRA NULL
    useEffect(() => {
        if (esContextoCliente && codigoBusqueda.trim()) {
            const terminoBusqueda = codigoBusqueda.toLowerCase();
            const filtradas = citasCliente.filter(cita => {
                // Asegurar que todos los valores sean string antes de aplicar toLowerCase o includes
                const codigo = cita.codigo || '';
                const estado = cita.estado || '';
                const estadoPago = cita.estado_pago || '';
                const total = cita.total?.toString() || '';
                const montoPagado = cita.monto_pagado?.toString() || '';

                return codigo.toLowerCase().includes(terminoBusqueda) ||
                    estado.toLowerCase().includes(terminoBusqueda) ||
                    estadoPago.toLowerCase().includes(terminoBusqueda) ||
                    total.includes(terminoBusqueda) ||
                    montoPagado.includes(terminoBusqueda);
            });
            setCitasFiltradas(filtradas);
        } else if (esContextoCliente) {
            setCitasFiltradas(citasCliente);
        }
    }, [codigoBusqueda, citasCliente, esContextoCliente]);

    const handleBuscar = async (codigo?: string) => {
        const codigoABuscar = codigo || codigoBusqueda;

        if (!codigoABuscar.trim()) {
            setError('Por favor ingrese un código de cita');
            return;
        }

        setIsLoading(true);
        setError(null);
        setIsDropdownOpen(false);

        try {
            const data = await buscarCitaPorCodigo(codigoABuscar);
            setReporte(data);
            // Si el código no está en las citas del cliente, lo añadimos al input
            if (esContextoCliente && !citasCliente.some(c => c.codigo === codigoABuscar)) {
                setCodigoBusqueda(codigoABuscar);
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Error al buscar la cita');
            setReporte(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSeleccionarCita = (cita: any) => {
        setCodigoBusqueda(cita.codigo);
        handleBuscar(cita.codigo);
    };

    const handleInputFocus = () => {
        if (esContextoCliente && citasCliente.length > 0) {
            setIsDropdownOpen(true);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleBuscar();
        } else if (e.key === 'Escape') {
            setIsDropdownOpen(false);
        }
    };

    const handleClearSearch = () => {
        setCodigoBusqueda('');
        setReporte(null);
        setError(null);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleExportarPDF = () => {
        if (reporte) {
            exportToPDF(reporte);
        }
    };

    const handleExportarExcel = () => {
        if (reporte) {
            exportToExcel(reporte);
        }
    };

    const getEstadoBadge = (estado: string) => {
        const estadoLower = estado?.toLowerCase() ?? '';
        switch (estadoLower) {
            case 'completo':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-[#8b3fa8]/10 text-[#61257b] border border-[#8b3fa8]/20">
                        <CheckCircle className="mr-1.5 h-3 w-3" />
                        Pagado
                    </span>
                );
            case 'parcial':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-[#ffd97c]/10 text-[#ea8406] border border-[#ffd97c]/20">
                        <AlertCircle className="mr-1.5 h-3 w-3" />
                        Parcial
                    </span>
                );
            default:
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-[#c1348b]/10 text-[#8f1f64] border border-[#c1348b]/20">
                        Pendiente
                    </span>
                );
        }
    };

    const formatFecha = (fechaStr: string) => {
        if (!fechaStr) return '';
        try {
            const fecha = new Date(fechaStr);
            return fecha.toLocaleDateString('es-BO', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return '';
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
            {/* Header con fondo blanco - Ahora incluye el botón de volver */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-gray-50 to-gray-100 p-8 border border-gray-200 shadow-xl">
                <div className="absolute -top-16 -right-16 w-80 h-80 bg-gradient-to-br from-[#c1348b]/20 via-[#c1348b]/10 to-[#61257b]/20 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-48 bg-gradient-to-r from-[#61257b]/5 via-transparent to-[#c1348b]/5 blur-2xl"></div>

                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
                        <div className="flex-1">
                            <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-[#61257b]/10 to-[#8b3fa8]/10 rounded-full border border-[#61257b]/20 mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-[#61257b] rounded-full"></div>
                                    <div className="w-2 h-2 bg-[#c1348b] rounded-full animate-pulse"></div>
                                    <div className="w-2 h-2 bg-[#8b3fa8] rounded-full"></div>
                                </div>
                                <span className="text-sm font-medium text-[#61257b]">Gestión de Pagos</span>
                            </div>

                            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                                <div>
                                    <h1 className="text-4xl font-bold text-gray-900 mb-3">
                                        Historial de Pagos por Sesiones
                                    </h1>
                                    <p className="text-gray-700 text-lg max-w-2xl">
                                        {esContextoCliente && usuarioInfo
                                            ? `Consultando pagos del cliente: ${usuarioInfo.nombre}`
                                            : 'Consulta el estado de pagos de una cita específica, organizado por sesiones'}
                                    </p>
                                    {esContextoCliente && usuarioInfo && (
                                        <p className="text-sm text-gray-600 mt-2">
                                            Buscador inteligente: Puedes seleccionar una cita del cliente o buscar cualquier código
                                        </p>
                                    )}
                                </div>

                                {/* Botón de volver */}
                                <div className="flex-shrink-0">
                                    {esContextoCliente ? (
                                        <Link
                                            to={`/kardex-citas/detalle/${usuarioId}`}
                                            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 hover:border-white/40 text-gray-700 hover:text-gray-900 transition-all duration-300 shadow-sm hover:shadow"
                                            title="Volver al detalle"
                                        >
                                            <ArrowLeft className="h-5 w-5" />
                                        </Link>
                                    ) : (
                                        <Link
                                            to="/kardex-citas"
                                            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 hover:border-white/40 text-gray-700 hover:text-gray-900 transition-all duration-300 shadow-sm hover:shadow"
                                            title="Volver al listado"
                                        >
                                            <ArrowLeft className="h-5 w-5" />
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Línea decorativa */}
                    <div className="mt-6 pt-6 border-t border-gray-200/50">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-3 h-3 rounded-full bg-[#61257b]"></div>
                                <span>Visualización detallada</span>
                            </div>
                            <div className="h-4 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-3 h-3 rounded-full bg-[#c1348b]"></div>
                                <span>Reportes exportables</span>
                            </div>
                            <div className="h-4 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-3 h-3 rounded-full bg-[#8b3fa8]"></div>
                                <span>Por sesión individual</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Buscador Inteligente - Una sola interfaz */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {esContextoCliente
                        ? 'Buscador inteligente de citas'
                        : 'Buscar por código de cita'}
                </label>

                <div className="relative" ref={dropdownRef}>
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={codigoBusqueda}
                                    onChange={(e) => {
                                        setCodigoBusqueda(e.target.value.toUpperCase());
                                        if (esContextoCliente) {
                                            setIsDropdownOpen(true);
                                        }
                                    }}
                                    onFocus={handleInputFocus}
                                    onKeyDown={handleInputKeyDown}
                                    placeholder={
                                        esContextoCliente
                                            ? 'Busca por código, estado, monto... o selecciona una cita del cliente'
                                            : 'Ej: CM/26/000001'
                                    }
                                    className="pl-10 pr-10 py-3 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#61257b] focus:border-[#61257b]"
                                />
                                {codigoBusqueda && (
                                    <button
                                        onClick={handleClearSearch}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Dropdown para citas del cliente */}
                            {esContextoCliente && isDropdownOpen && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                                    {isLoadingCitas ? (
                                        <div className="p-4 text-center text-gray-500">
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#61257b] mx-auto mb-2"></div>
                                            Cargando citas del cliente...
                                        </div>
                                    ) : citasFiltradas.length === 0 ? (
                                        <div className="p-4 text-center text-gray-500">
                                            {codigoBusqueda
                                                ? 'No se encontraron citas con ese criterio'
                                                : 'No hay citas registradas para este cliente'}
                                        </div>
                                    ) : (
                                        <div className="py-2">
                                            <div className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                                                {citasFiltradas.length} {citasFiltradas.length === 1 ? 'cita encontrada' : 'citas encontradas'}
                                            </div>
                                            {citasFiltradas.map((cita) => (
                                                <div
                                                    key={cita.id}
                                                    onClick={() => handleSeleccionarCita(cita)}
                                                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="font-medium text-gray-900 truncate">
                                                                    {cita.codigo}
                                                                </span>
                                                                <span className={`px-2 py-1 text-xs rounded-full shrink-0 ${
                                                                    cita.estado === 'Confirmada'
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : cita.estado === 'Cancelada'
                                                                            ? 'bg-red-100 text-red-800'
                                                                            : 'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                    {cita.estado}
                                                                </span>
                                                                <span className={`px-2 py-1 text-xs rounded-full shrink-0 ${
                                                                    cita.estado_pago === 'pagado completo'
                                                                        ? 'bg-blue-100 text-blue-800'
                                                                        : cita.estado_pago === 'pagado parcial'
                                                                            ? 'bg-orange-100 text-orange-800'
                                                                            : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {cita.estado_pago}
                                                                </span>
                                                            </div>

                                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                                <div>
                                                                    <span className="text-gray-500">Fecha: </span>
                                                                    <span className="font-medium">{formatFecha(cita.fecha_inicio)}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Total: </span>
                                                                    <span className="font-medium">Bs {cita.total.toFixed(2)}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Pagado: </span>
                                                                    <span className="font-medium text-[#61257b]">Bs {cita.monto_pagado.toFixed(2)}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Saldo: </span>
                                                                    <span className={`font-medium ${
                                                                        (cita.total - cita.monto_pagado) > 0
                                                                            ? 'text-[#c1348b]'
                                                                            : 'text-[#8b3fa8]'
                                                                    }`}>
                                                                        Bs {(cita.total - cita.monto_pagado).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="ml-4 flex-shrink-0">
                                                            <ChevronRight className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center">
                            <button
                                onClick={() => handleBuscar()}
                                disabled={isLoading || !codigoBusqueda}
                                className="px-6 py-3 bg-[#61257b] text-white font-medium rounded-lg hover:bg-[#0066a6] focus:outline-none focus:ring-2 focus:ring-[#61257b] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors duration-300 shadow-sm hover:shadow min-w-[120px] justify-center"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                        Buscando...
                                    </>
                                ) : (
                                    <>
                                        <Search className="h-4 w-4" />
                                        Buscar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {esContextoCliente && !isDropdownOpen && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-[#61257b]"></div>
                                <span>Presiona la tecla ↓ para ver citas del cliente</span>
                            </div>
                            <div className="h-3 w-px bg-gray-300"></div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-[#c1348b]"></div>
                                <span>O escribe cualquier código de cita</span>
                            </div>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700 text-sm">{error}</p>
                    </div>
                )}
            </div>

            {/* Reporte - Mantenido igual */}
            {reporte && (
                <div className="space-y-8">
                    {/* Resumen */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#61257b]/5 to-[#8b3fa8]/5">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                        Cita: {reporte.codigo}
                                    </h2>
                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                        <span className="flex items-center gap-1">
                                            <User className="h-4 w-4" />
                                            {reporte.cliente_info.nombre}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <DollarSign className="h-4 w-4" />
                                            Total: Bs {Number(reporte.total).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={handleExportarPDF}
                                        className="px-4 py-2.5 bg-[#c1348b] text-white font-medium rounded-lg hover:bg-[#e01c4a] focus:outline-none focus:ring-2 focus:ring-[#c1348b] focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors duration-300 shadow-sm hover:shadow"
                                    >
                                        <FileText className="h-4 w-4" />
                                        Exportar PDF
                                    </button>
                                    <button
                                        onClick={handleExportarExcel}
                                        className="px-4 py-2.5 bg-[#8b3fa8] text-white font-medium rounded-lg hover:bg-[#00b8a0] focus:outline-none focus:ring-2 focus:ring-[#8b3fa8] focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors duration-300 shadow-sm hover:shadow"
                                    >
                                        <FileSpreadsheet className="h-4 w-4" />
                                        Exportar Excel
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                    <p className="text-sm font-medium text-gray-600 mb-1">Total Pagado</p>
                                    <p className="text-2xl font-bold text-[#61257b]">
                                        Bs {Number(reporte.monto_pagado).toFixed(2)}
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                    <p className="text-sm font-medium text-gray-600 mb-1">Deuda Total</p>
                                    <p className={`text-2xl font-bold ${reporte.deuda_total > 0 ? 'text-[#c1348b]' : 'text-[#8b3fa8]'}`}>
                                        Bs {Number(reporte.deuda_total).toFixed(2)}
                                    </p>
                                    {reporte.deuda_total <= 0 && (
                                        <p className="text-xs text-[#8b3fa8] mt-1 flex items-center gap-1">
                                            <CheckCircle className="h-3 w-3" />
                                            Sin deudas pendientes
                                        </p>
                                    )}
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                    <p className="text-sm font-medium text-gray-600 mb-1">Porcentaje Pagado</p>
                                    <p className="text-2xl font-bold text-[#8f1f64]">
                                        {((reporte.monto_pagado / reporte.total) * 100).toFixed(1)}%
                                    </p>
                                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                        <div
                                            className="bg-gradient-to-r from-[#8b3fa8] to-[#61257b] h-2 rounded-full"
                                            style={{ width: `${(reporte.monto_pagado / reporte.total) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            {/* Detalle por Sesiones */}
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalle por Sesiones</h3>
                            <div className="space-y-4">
                                {reporte.sesiones_detalle.map((sesion, index) => (
                                    <div key={sesion.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <Calendar className="h-5 w-5 text-gray-500" />
                                                <div>
                                                    <h4 className="font-medium text-gray-900">
                                                        Sesión {index + 1}: {new Date(sesion.fecha_inicio).toLocaleDateString('es-BO', { timeZone: 'UTC' })}
                                                        {sesion.fecha_fin !== sesion.fecha_inicio &&
                                                            ` - ${new Date(sesion.fecha_fin).toLocaleDateString('es-BO', { timeZone: 'UTC' })}`
                                                        }
                                                    </h4>
                                                    <p className="text-sm text-gray-600">
                                                        {sesion.horas} hora{sesion.horas !== 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="text-right">
                                                    <p className="text-sm text-gray-600">Precio con descuento</p>
                                                    <p className="font-medium text-gray-900">
                                                        Bs {Number(sesion.precio_con_descuento).toFixed(2)}
                                                    </p>
                                                </div>
                                                {getEstadoBadge(sesion.estado_pago)}
                                            </div>
                                        </div>

                                        <div className="p-4">
                                            {/* Estado de pago de la sesión */}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div className="text-center p-3 bg-gradient-to-r from-[#61257b]/5 to-[#8b3fa8]/5 rounded-lg border border-[#8b3fa8]/20">
                                                    <p className="text-sm text-gray-600">Pagado en esta sesión</p>
                                                    <p className="text-lg font-semibold text-[#61257b]">
                                                        Bs {Number(sesion.monto_pagado).toFixed(2)}
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                    <p className="text-sm text-gray-600">Saldo pendiente</p>
                                                    <p className={`text-lg font-semibold ${sesion.saldo_pendiente > 0 ? 'text-[#c1348b]' : 'text-[#8b3fa8]'}`}>
                                                        Bs {Number(sesion.saldo_pendiente).toFixed(2)}
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                    <p className="text-sm text-gray-600">Pagos registrados</p>
                                                    <p className="text-lg font-semibold text-gray-900">
                                                        {sesion.pagos.length}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Historial de pagos de esta sesión */}
                                            {sesion.pagos.length > 0 ? (
                                                <div>
                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Pagos realizados:</h5>
                                                    <div className="space-y-2">
                                                        {sesion.pagos.map((pago) => (
                                                            <div key={pago.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                                <div className="flex items-center gap-3">
                                                                    <CreditCard className="h-4 w-4 text-gray-500" />
                                                                    <div>
                                                                        <p className="text-sm font-medium text-gray-900">
                                                                            {new Date(pago.fecha_pago).toLocaleDateString('es-BO')}
                                                                            {' '}
                                                                            {new Date(pago.fecha_pago).toLocaleTimeString('es-BO', {
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            })}
                                                                        </p>
                                                                        <p className="text-xs text-gray-600">{pago.tipo_pago_nombre}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-sm font-semibold text-[#61257b]">
                                                                        Bs {Number(pago.monto).toFixed(2)}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">Código: {pago.codigo}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-4 text-gray-500">
                                                    No se han registrado pagos específicos para esta sesión
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Historial completo de pagos */}
                            <div className="mt-8">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial Completo de Pagos</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gradient-to-r from-[#61257b]/5 to-[#8b3fa8]/5">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Fecha y Hora
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Origen
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Tipo de Pago
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Monto
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Código
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {reporte.historial_pagos.map((pago) => (
                                                <tr key={pago.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {new Date(pago.fecha_pago).toLocaleDateString('es-BO', { timeZone: 'UTC' })}
                                                        <br />
                                                        <span className="text-gray-500">
                                                            {new Date(pago.fecha_pago).toLocaleTimeString('es-BO', {
                                                                timeZone: 'UTC',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {pago.origen}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {pago.tipo_pago_nombre}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#61257b]">
                                                        Bs {Number(pago.monto).toFixed(2)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                                        {pago.codigo}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Estado inicial */}
            {!reporte && !error && !isLoading && (
                <div className="text-center py-16">
                    <div className="mx-auto w-20 h-20 bg-[#61257b]/10 rounded-full flex items-center justify-center mb-6">
                        <Search className="h-10 w-10 text-[#61257b]" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {esContextoCliente
                            ? 'Busca una cita para ver su historial de pagos'
                            : 'Busca una cita para ver su historial de pagos'}
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                        {esContextoCliente
                            ? 'Utiliza el buscador inteligente para seleccionar una cita del cliente o buscar cualquier código.'
                            : 'Ingresa el código de una cita en el buscador superior para consultar el estado de pagos organizado por sesiones.'}
                    </p>
                </div>
            )}
        </div>
    );
};

// Componente auxiliar para el icono de flecha
const ChevronRight = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
);

export default HistorialPagosPage;